/**
 * SHINE Cart Drawer
 * AJAX cart operations - add to cart, update quantity, remove items.
 * Opens a premium right-side drawer without page reload.
 */
(function () {
  'use strict';

  const selectors = {
    drawer: '#cart-drawer',
    overlay: '#cart-drawer-overlay',
    closeBtn: '#cart-drawer-close',
    body: '#cart-drawer-body',
    footer: '#cart-drawer-footer',
    count: '#cart-drawer-count',
    subtotal: '#cart-drawer-subtotal',
    headerCartCount: '.header-cart-count',
    cartToggle: '[data-cart-toggle]',
  };

  let isUpdating = false;

  // ─── Drawer Open / Close ───────────────────────────────────

  function openDrawer(skipFetch = false) {
    const drawer = document.querySelector(selectors.drawer);
    const overlay = document.querySelector(selectors.overlay);
    if (!drawer || !overlay) return;

    drawer.classList.add('is-open');
    overlay.classList.add('is-visible');
    document.body.style.overflow = 'hidden';
    drawer.setAttribute('aria-hidden', 'false');

    if (!skipFetch) {
      fetchCart()
        .then((cart) => {
          renderDrawer(cart);
        })
        .catch(console.error);
    }
  }

  function closeDrawer() {
    const drawer = document.querySelector(selectors.drawer);
    const overlay = document.querySelector(selectors.overlay);
    if (!drawer || !overlay) return;

    drawer.classList.remove('is-open');
    overlay.classList.remove('is-visible');
    document.body.style.overflow = '';
    drawer.setAttribute('aria-hidden', 'true');
  }

  // ─── Cart API Helpers ──────────────────────────────────────

  function fetchCart() {
    return fetch('/cart.js?t=' + Date.now(), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }).then((res) => res.json());
  }

  function addToCart(formData) {
    return fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
    }).then((res) => {
      if (!res.ok) throw new Error('Add to cart failed');
      return res.json();
    });
  }

  function changeItem(line, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity }),
    }).then((res) => res.json());
  }

  // ─── Render Cart Drawer ────────────────────────────────────

  function renderDrawer(cart) {
    const body = document.querySelector(selectors.body);
    const footer = document.querySelector(selectors.footer);
    const countEl = document.querySelector(selectors.count);
    const subtotalEl = document.querySelector(selectors.subtotal);

    // Update header cart count badge(s)
    document.querySelectorAll(selectors.headerCartCount).forEach((el) => {
      el.textContent = cart.item_count;
      if (cart.item_count === 0) {
        el.style.display = 'none';
      } else {
        el.style.display = 'flex';
      }
    });

    // Update drawer count
    if (countEl) countEl.textContent = cart.item_count;

    if (cart.item_count === 0) {
      // Empty state
      body.innerHTML = `
        <div class="cart-drawer__empty" id="cart-drawer-empty">
          <svg class="h-16 w-16 opacity-20 mb-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="0.75" stroke-linecap="round" stroke-linejoin="round">
            <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"></path>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <path d="M16 10a4 4 0 0 1-8 0"></path>
          </svg>
          <p class="cart-drawer__empty-title">Your bag is empty</p>
          <p class="cart-drawer__empty-subtitle">Explore our collection and find something you love.</p>
          <a href="/collections/all" class="cart-drawer__shop-btn">Continue Shopping</a>
        </div>
      `;
      if (footer) footer.style.display = 'none';
      return;
    }

    // Build items HTML
    let itemsHtml = '<ul class="cart-drawer__items" id="cart-drawer-items">';
    cart.items.forEach((item, index) => {
      const line = index + 1;
      let rawImgUrl = '';
      if (item.image) {
        rawImgUrl = item.image;
      } else if (item.featured_image) {
        if (typeof item.featured_image === 'string') {
          rawImgUrl = item.featured_image;
        } else if (item.featured_image.url) {
          rawImgUrl = item.featured_image.url;
        }
      }
      const imageUrl = rawImgUrl ? getSizedImageUrl(rawImgUrl, '200x') : '';
      const variantTitle =
        item.variant_title && item.variant_title !== 'Default Title'
          ? `<p class="cart-drawer__item-variant">${item.variant_title}</p>`
          : '';

      itemsHtml += `
        <li class="cart-drawer__item" data-line="${line}" data-key="${item.key}">
          <a href="${item.url}" class="cart-drawer__item-image">
            ${
              imageUrl
                ? `<img src="${imageUrl}" alt="${item.product_title}" class="w-full h-full object-cover" loading="lazy">`
                : `<div class="cart-drawer__item-placeholder">
                    <svg class="h-6 w-6 opacity-30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"></path>
                      <line x1="3" y1="6" x2="21" y2="6"></line>
                      <path d="M16 10a4 4 0 0 1-8 0"></path>
                    </svg>
                  </div>`
            }
          </a>
          <div class="cart-drawer__item-details">
            <a href="${item.url}" class="cart-drawer__item-title">${item.product_title}</a>
            ${variantTitle}
            <p class="cart-drawer__item-price">${formatMoney(item.final_line_price)}</p>
            <div class="cart-drawer__item-actions">
              <div class="cart-drawer__qty">
                <button type="button" class="cart-drawer__qty-btn" data-action="decrease" aria-label="Decrease quantity">
                  <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M20 12H4"/>
                  </svg>
                </button>
                <span class="cart-drawer__qty-value">${item.quantity}</span>
                <button type="button" class="cart-drawer__qty-btn" data-action="increase" aria-label="Increase quantity">
                  <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 4v16m8-8H4"/>
                  </svg>
                </button>
              </div>
              <button type="button" class="cart-drawer__remove" data-action="remove" aria-label="Remove item">Remove</button>
            </div>
          </div>
        </li>
      `;
    });
    itemsHtml += '</ul>';

    body.innerHTML = itemsHtml;

    // Update footer
    if (footer) {
      footer.style.display = '';
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
    }

    // Re-bind item events
    bindItemEvents();
  }

  // ─── Item Event Handlers ───────────────────────────────────

  function bindItemEvents() {
    const items = document.querySelectorAll('.cart-drawer__item');
    items.forEach((item) => {
      const line = parseInt(item.dataset.line);

      // Quantity buttons
      item.querySelectorAll('.cart-drawer__qty-btn').forEach((btn) => {
        btn.addEventListener('click', function () {
          if (isUpdating) return;
          const action = this.dataset.action;
          const qtyEl = item.querySelector('.cart-drawer__qty-value');
          let qty = parseInt(qtyEl.textContent);

          if (action === 'increase') qty++;
          else if (action === 'decrease') qty = Math.max(0, qty - 1);

          updateLineItem(line, qty);
        });
      });

      // Remove button
      const removeBtn = item.querySelector('.cart-drawer__remove');
      if (removeBtn) {
        removeBtn.addEventListener('click', function () {
          if (isUpdating) return;
          updateLineItem(line, 0);
        });
      }
    });
  }

  function updateLineItem(line, quantity) {
    if (isUpdating) return;
    isUpdating = true;

    // Show loading state on item
    const itemEl = document.querySelector(`.cart-drawer__item[data-line="${line}"]`);
    if (itemEl) itemEl.style.opacity = '0.5';

    changeItem(line, quantity)
      .then((cart) => {
        renderDrawer(cart);
      })
      .catch(console.error)
      .finally(() => {
        isUpdating = false;
      });
  }

  // ─── AJAX Add To Cart (intercept all forms) ────────────────

  function interceptAddToCartForms() {
    document.addEventListener('submit', function (e) {
      const form = e.target;
      if (!form.matches('form[action="/cart/add"]')) return;

      // Skip if data-no-ajax is set (for Buy Now etc.)
      if (form.dataset.noAjax) return;

      e.preventDefault();

      const submitBtn = form.querySelector('[type="submit"]');
      const btnText = submitBtn ? submitBtn.querySelector('.btn-text') || submitBtn : null;
      const originalText = btnText ? btnText.textContent : '';

      // Loading state
      if (submitBtn) submitBtn.disabled = true;
      if (btnText) btnText.textContent = 'Adding...';

      const formData = new FormData(form);

      addToCart(formData)
        .then(() => {
          // Success feedback
          if (btnText) btnText.textContent = 'Added ✓';

          // Fetch updated cart and render + open drawer
          return fetchCart();
        })
        .then((cart) => {
          renderDrawer(cart);
          openDrawer(true);
        })
        .catch((err) => {
          console.error('Add to cart error:', err);
          if (btnText) btnText.textContent = 'Error – Try Again';
        })
        .finally(() => {
          // Reset button state after delay
          setTimeout(() => {
            if (submitBtn) submitBtn.disabled = false;
            if (btnText) btnText.textContent = originalText;
          }, 1500);
        });
    });
  }

  function initQuickAdd() {
    document.addEventListener('click', function (e) {
      const quickAddBtn = e.target.closest('[data-quick-add]');
      if (!quickAddBtn) return;

      e.preventDefault();
      e.stopPropagation();

      const variantId = quickAddBtn.dataset.quickAdd;
      if (!variantId) return;

      // Loading state on button
      const originalHtml = quickAddBtn.innerHTML;
      quickAddBtn.disabled = true;
      quickAddBtn.innerHTML = 'Adding...';

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);

      addToCart(formData)
        .then(() => {
          quickAddBtn.innerHTML = 'Added ✓';
          return fetchCart();
        })
        .then((cart) => {
          renderDrawer(cart);
          openDrawer(true);
        })
        .catch((err) => {
          console.error('Quick add error:', err);
          quickAddBtn.innerHTML = 'Error';
        })
        .finally(() => {
          setTimeout(() => {
            quickAddBtn.disabled = false;
            quickAddBtn.innerHTML = originalHtml;
          }, 1500);
        });
    });
  }

  // ─── Money Formatter ───────────────────────────────────────

  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    // Fallback: simple cents → dollars
    const amount = (cents / 100).toFixed(2);
    return '$' + amount;
  }

  // ─── Image URL Helper ──────────────────────────────────────

  function getSizedImageUrl(url, size) {
    if (!url) return '';
    // Shopify image URL resizing
    const match = url.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif|webp)(\?v=\d+)?$/i);
    if (match) {
      const prefix = url.split(match[0]);
      return prefix[0] + '_' + size + '.' + match[1] + (match[2] || '');
    }
    return url;
  }

  // ─── Init ──────────────────────────────────────────────────

  function init() {
    // Close button
    const closeBtn = document.querySelector(selectors.closeBtn);
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);

    // Overlay click
    const overlay = document.querySelector(selectors.overlay);
    if (overlay) overlay.addEventListener('click', closeDrawer);

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeDrawer();
    });

    // Cart toggle (header icon)
    document.querySelectorAll(selectors.cartToggle).forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        openDrawer();
      });
    });

    // Intercept all add-to-cart forms
    interceptAddToCartForms();

    // Initialize quick-add delegated handler
    initQuickAdd();

    // Bind quantity/remove events on initial items
    bindItemEvents();

    // Fetch and render cart on page load to initialize drawer and counts
    fetchCart()
      .then((cart) => {
        renderDrawer(cart);
      })
      .catch(console.error);
  }

  // Wait for DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
